import { useMutation, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import catchError from '../../../components/global/CatchError'
import Loader from '../../../components/global/Loader'
import SkillCard from '../../../components/global/SkillCard'
import Title from '../../../components/global/Title'
import { SERVER_URL, apiHeader } from '../../../config'
import UserContext from '../../../context/UserContext'
import { useLangSelected } from '../../../hooks/useLangSelected'
import { Skill } from '../../../types/skill.type'
import { TitleWrapper } from '../../admin/coach/AdminCoach'
import { StyledButton } from '../../admin/Login'
import { SkillsWrapper } from '../my-members/CoachMyMembers'
import CoachSkillFormModal from './CoachSkillFormModal'

export default function CoachSkills() {
    const user = useContext(UserContext)
    const { t } = useTranslation()

    const [skillToEdit, setSkillToEdit] = useState<Skill>()
    const [showEditSkillModal, setShowEditSkillModal] = useState(false)

    const { data: skills, refetch: refetchSkills } = useQuery({
        queryKey: ['skills'],
        queryFn: async () => {
            const response = await axios.get<Skill[]>(`${SERVER_URL}/skills`, apiHeader(user.tokenCoach))
            return response.data
        },
    })

    const navigate = useNavigate()
    const lng = useLangSelected()

    const { mutate: deleteSkill } = useMutation({
        mutationFn: async (id: number) => {
            await axios.delete(`${SERVER_URL}/skills/${id}`, apiHeader(user.tokenCoach))
            refetchSkills()
            toast.success(t('coach.skill-deleted'))
        },
        onError: (error: any) => {
            if (error.response?.status === 401) {
                catchError({ status: 401, message: 'Votre session a expiré' }, () => {
                    localStorage.removeItem('tokenCoach')
                    navigate(`/${lng}/login`)
                })
            } else {
                console.error('Erreur lors de la suppression de la compétence:', error)
                toast.error(t('coach.skill-delete-error'))
            }
        },
    })

    if (!skills) return <Loader />

    const editSkill = (skill: Skill) => {
        setSkillToEdit(skill)
        setShowEditSkillModal(true)
    }

    return (
        <div>
            <TitleWrapper>
                <Title>{t('coach.skills')}</Title>
                <StyledButton
                    className="btn btn-primary"
                    onClick={() => {
                        setShowEditSkillModal(true)
                    }}
                >
                    Créer une compétence
                </StyledButton>
            </TitleWrapper>
            <SkillsWrapper>
                {skills.map((skill) => (
                    <SkillCard
                        key={skill.id}
                        name={skill.name}
                        deleteFn={() => deleteSkill(skill.id)}
                        editFn={() => editSkill(skill)}
                    />
                ))}
            </SkillsWrapper>
            {showEditSkillModal && (
                <CoachSkillFormModal
                    refetchCoachSkills={refetchSkills}
                    setShowEditSkillModal={setShowEditSkillModal}
                    skillToEdit={skillToEdit}
                    setSkillToEdit={setSkillToEdit}
                    skills={skills}
                />
            )}
        </div>
    )
}
